import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, AlertTriangle, Loader2, Check, X, Clock } from "lucide-react";
import { verificarPorCodigo } from "../services/assinaturas";
import { useBranding } from "../hooks/useBranding";

export function Verificar() {
  const { codigo } = useParams<{ codigo: string }>();
  const branding = useBranding();
  const [busca, setBusca] = useState(codigo || "");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["verificar", busca],
    queryFn: () => verificarPorCodigo(busca),
    enabled: !!busca,
    retry: false,
  });

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950">
      <header className="bg-slate-900 text-white px-4 sm:px-6 py-3 flex items-center gap-3 shadow">
        <ShieldCheck size={20} className="text-emerald-300" />
        <div>
          <p className="text-sm font-bold">{branding?.nome || "RECDC OS"}</p>
          <p className="text-[11px] text-slate-300">Verificação pública de documento</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 sm:p-6 space-y-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <p className="text-sm text-slate-700 dark:text-slate-200">
            Insira o código que aparece no documento (ex: <code className="font-mono">GS-A1B2-C3D4</code>) para conferir a autenticidade.
          </p>
          <div className="flex gap-2 mt-3">
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value.toUpperCase())}
              placeholder="GS-XXXX-XXXX"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 outline-none font-mono text-sm focus:border-blue-500"
            />
            <button
              onClick={() => refetch()}
              disabled={!busca}
              className="px-4 py-2 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              Verificar
            </button>
          </div>
        </div>

        {isLoading && busca && (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-blue-600" size={28} /></div>
        )}

        {error && busca && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
            <AlertTriangle size={28} className="text-red-500 mx-auto mb-2" />
            <p className="font-semibold text-red-700">Documento não encontrado</p>
            <p className="text-sm text-red-600 mt-1">Confira o código informado.</p>
          </div>
        )}

        {data && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-3">
            <div className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-emerald-600" />
              <p className="font-semibold text-slate-800 dark:text-slate-100">Documento autêntico</p>
            </div>

            <Linha label="Título">{data.titulo}</Linha>
            <Linha label="Status">
              <span className={`inline-block text-[10px] uppercase font-bold px-2 py-0.5 rounded ${data.status === "assinado" ? "bg-emerald-100 text-emerald-700" : data.status === "importado" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                {data.status}
              </span>
              {data.importado && <span className="ml-2 text-[11px] text-slate-500">(assinado externamente)</span>}
            </Linha>
            <Linha label="Projeto">{data.projeto.nome}</Linha>
            <Linha label="Cliente">{data.projeto.cliente}</Linha>
            <Linha label="Código">{data.codigoVerificacao}</Linha>
            <Linha label="Hash original (SHA-256)">
              <span className="font-mono text-[10px] break-all">{data.arquivoHash}</span>
            </Linha>
            {data.arquivoFinalHash && data.arquivoFinalHash !== data.arquivoHash && (
              <Linha label="Hash final">
                <span className="font-mono text-[10px] break-all">{data.arquivoFinalHash}</span>
              </Linha>
            )}
            {data.finalizadoEm && <Linha label="Finalizado em">{new Date(data.finalizadoEm).toLocaleString("pt-BR")}</Linha>}
            {data.criadoEm && <Linha label="Criado em">{new Date(data.criadoEm).toLocaleString("pt-BR")}</Linha>}

            <div className="border-t border-slate-100 dark:border-slate-700 pt-3 mt-2">
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Signatários ({data.signatarios.length})</p>
              <div className="space-y-1.5">
                {data.signatarios.map((s: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-2 text-xs">
                    {s.status === "assinado" ? <Check size={12} className="text-emerald-600" /> : s.status === "recusado" ? <X size={12} className="text-red-500" /> : <Clock size={12} className="text-amber-500" />}
                    <span className="font-medium text-slate-700 dark:text-slate-200">{s.nome}</span>
                    <span className="text-slate-500">{s.email}</span>
                    {s.papel && <span className="text-slate-400">· {s.papel}</span>}
                    <span className="ml-auto text-[10px] uppercase text-slate-400">{s.status}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-[10px] text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-700">
              Os dados sensíveis dos signatários são parcialmente mascarados em respeito à LGPD. O documento integral só é acessível pelos próprios signatários.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function Linha({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="text-sm">
      <span className="text-xs text-slate-500 dark:text-slate-400">{label}:</span>{" "}
      <span className="text-slate-800 dark:text-slate-100">{children}</span>
    </div>
  );
}
